import type { Theme } from '@mui/material/styles';

// The Query Builder in the Stroma design (#565): the mock's .qtop · .mbrowser ·
// .panel/.ph · .qchip · .dock-res · .wagent, on the design tokens.

export const QB_BROWSER_WIDTH = 300;

export const qbWorkspace = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  bgcolor: 'var(--ground)',
} as const;

export const qbTopBar = {
  bgcolor: 'var(--surface)',
  backgroundImage: 'none',
  color: 'var(--ink)',
  border: 0,
  borderBottom: '1px solid var(--line)',
  borderRadius: 0,
  boxShadow: 'none',
} as const;

export const qbTopBarToolbar = {
  gap: 1.5,
  px: { xs: 2, sm: 2.25 },
  py: 1.25,
  minHeight: 64,
  flexWrap: 'wrap',
} as const;

export const qbTitle = {
  fontFamily: 'var(--display)',
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--ink)',
  letterSpacing: '-0.005em',
  whiteSpace: 'nowrap',
} as const;

export const qbIconButton = {
  width: 30,
  height: 30,
  borderRadius: '8px',
  color: 'var(--ink-2)',
  border: '1px solid transparent',
  '&:hover': { bgcolor: 'var(--sunk)', borderColor: 'var(--line)' },
} as const;

export const qbIconButtonOn = {
  ...qbIconButton,
  bgcolor: 'var(--accent-soft)',
  color: 'var(--accent-ink)',
  '&:hover': { bgcolor: 'var(--accent-soft)', borderColor: 'var(--line)' },
} as const;

export const qbBrowser = {
  width: QB_BROWSER_WIDTH,
  minWidth: QB_BROWSER_WIDTH,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  bgcolor: 'var(--surface2)',
  backgroundImage: 'none',
  border: 0,
  borderRight: '1px solid var(--line)',
  borderRadius: 0,
  boxShadow: 'none',
} as const;

export const qbBrowserCollapsed = {
  width: 36,
  flexShrink: 0,
  borderRight: '1px solid var(--line)',
  bgcolor: 'var(--surface2)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  pt: 1,
  cursor: 'pointer',
  '&:hover': { bgcolor: 'var(--sunk)' },
} as const;

export const qbBrowserHeader = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1.5,
  pt: 1.25,
  pb: 0.5,
  flexShrink: 0,
} as const;

export const qbBrowserTitle = {
  fontSize: 13.5,
  fontWeight: 700,
  color: 'var(--ink)',
  letterSpacing: 0,
  textTransform: 'none',
} as const;

export const qbCards = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.75,
  px: { xs: 2, sm: 2.25 },
  py: 2,
  overflow: 'auto',
  minWidth: 0,
} as const;

export const qbCard = {
  m: 0,
  p: 0,
  bgcolor: 'var(--surface)',
  backgroundImage: 'none',
  border: '1px solid var(--line)',
  borderRadius: '12px',
  boxShadow: 'none',
  flexShrink: 0,
} as const;

export const qbCardHeader = {
  m: 0,
  px: 2,
  py: 1.5,
  borderBottom: '1px solid var(--line-soft)',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
  '& .MuiSvgIcon-root': { fontSize: 17, color: 'var(--ink-3)' },
} as const;

export const qbCardBody = { px: 2, py: 1.75 } as const;

export const qbEmpty = { fontSize: 12.5, color: 'var(--ink-3)' } as const;

// The mock's dimension ink is a violet that is not a palette token; keep it in
// one place, readable in both modes.
export const qbDimensionInk = (theme: Theme) => (theme.palette.mode === 'dark' ? '#D39BF0' : '#8B3FB0');

const qbChipBase = {
  height: 'auto',
  minHeight: 26,
  borderRadius: '999px',
  bgcolor: 'var(--surface)',
  boxShadow: 'none',
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: 0,
  '& .MuiChip-label': { px: 1, py: 0.25 },
  '& .MuiChip-deleteIcon': { fontSize: 16, color: 'var(--ink-3)', '&:hover': { color: 'var(--ink)' } },
} as const;

export const qbMetricChip = {
  ...qbChipBase,
  color: 'var(--accent-ink)',
  borderColor: 'color-mix(in srgb, var(--accent) 45%, var(--line))',
  '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
} as const;

export const qbDimensionChip = (theme: Theme) => ({
  ...qbChipBase,
  color: qbDimensionInk(theme),
  borderColor: `color-mix(in srgb, ${qbDimensionInk(theme)} 40%, var(--line))`,
  '&:hover': { bgcolor: 'var(--sunk)' },
});

export const qbCalcChip = {
  ...qbChipBase,
  color: 'var(--agent)',
  borderColor: 'var(--agent)',
} as const;

// .mc — the small inline badge inside a chip (aggregation · period · alias · use).
export const qbBadge = (tone: 'info' | 'warn' | 'plain' | 'metric' | 'dimension') => ({
  height: 18,
  borderRadius: '4px',
  border: 0,
  boxShadow: 'none',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0,
  '& .MuiChip-label': { px: 0.625 },
  ...(tone === 'info' ? { bgcolor: 'var(--agent-tint)', color: 'var(--agent)' }
    : tone === 'warn' ? { bgcolor: 'var(--warn-tint)', color: 'var(--warn)' }
      : tone === 'metric' ? { bgcolor: 'var(--accent-soft)', color: 'var(--accent-ink)' }
        : { bgcolor: 'var(--dim-tint)', color: 'var(--ink-2)' }),
});

export const qbSortBadge = { color: 'var(--ok)', fontWeight: 700 } as const;

export const qbResultsDock = {
  bgcolor: 'var(--surface)',
  backgroundImage: 'linear-gradient(180deg, var(--surface), var(--surface2))',
  border: 0,
  borderTop: '1px solid var(--line)',
  borderRadius: 0,
  boxShadow: '0 -12px 30px -18px var(--shadow-c)',
} as const;

export const qbResultsTitle = {
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'none',
  color: 'var(--ink)',
} as const;

export const qbResultTab = (active: boolean) => ({
  px: 1.25,
  py: 0.75,
  fontSize: 12.5,
  fontWeight: 600,
  color: active ? 'var(--ink)' : 'var(--ink-3)',
  borderBottom: '2px solid',
  borderBottomColor: active ? 'var(--accent)' : 'transparent',
  bgcolor: 'transparent',
  '&:hover': { color: 'var(--ink)' },
});

export const qbAgentPanel = {
  borderLeft: '1px solid var(--line)',
  bgcolor: 'var(--surface)',
  backgroundImage: 'linear-gradient(180deg, var(--surface), var(--surface2))',
} as const;

export const qbAgentHeader = {
  px: 1.75,
  py: 1.5,
  borderBottom: '1px solid var(--line-soft)',
} as const;
